import {
  ChevronDoubleDownIcon,
  InformationCircleIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { Button } from "V2.0/common/button";
import { Dialog, DialogTitle, DialogBody } from "V2.0/common/dialog";
import Joi from "joi-browser";
import Form from "V2.0/components/Form";
import { message, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { createAssistant } from "./service";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownHeader,
  DropdownHeading,
} from "V2.0/common/dropdown";
import { Icons } from "./AssistantsTable";
import { RiInformation2Line } from "react-icons/ri";
import { FaCircleInfo, FaInfo } from "react-icons/fa6";

const title = {
  instagram: "Create Instagram Assistant",
  whatsapp: "Create Whatsapp Assistant",
  sms: "Create SMS Assistant",
  voice: "Create Voice Assistant",
  messenger: "Create Messenger Assistant",
  chat: "Create Chat Embed Assistant",
  //email
  //chat
};

const assistantTypes = [
  "Voice",
  "SMS",
  "WhatsApp",
  "Messenger",
  "Instagram",
  "Chat",
];

const AssistantType = (props) => {
  const { type } = props;

  return (
    <div className="flex items-center gap-x-2">
      <div>{Icons[type.toLowerCase()]}</div>
      <p className="text-sm">{type === "Chat" ? "Chat Embed" : type}</p>
    </div>
  );
};

const template = [
  { name: "name", label: "Assistant Name", type: "text" },
  {
    name: "description",
    label: "Description",
    type: "textArea",
    style: { height: "100px", fontSize: "13px" },
    description: "Describe Who the assistant is?",
  },
];

const schema = {
  name: Joi.string().required(),
  description: Joi.string().allow(null, ""),
  assistantType: Joi.string().required(),
};

const buttonInfo = {
  buttons: "2",
  primaryLabel: "Create",
  secondaryLabel: "Cancel",
};

export function AssistantCreate(props) {
  const { allowAssistantCreation, projectId, remainingAssistants, onAssistantCreationSuccess } = props;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [assistantData, setAssistantData] = useState({
    name: "",
    description: "",
    assistantType: "",
  });
  const navigate = useNavigate();

  const updateAssistantType = (assistantType) => {
    setAssistantData((prevState) => {
      return {
        ...prevState,
        assistantType,
      };
    });
  };

  const handleDropdownItemClick = (item) => {
    updateAssistantType(item);
    setIsOpen(true);
  };

  const handleCreate = async (payload) => {
    message.loading("Creating Assistant");
    try {
      const response = await createAssistant({ projectId, payload });
      if (response.status === 201) {
        message.destroy();
        message.success("Assistant Created");
        const { _id } = response.data;
        setLoading(false);
        onAssistantCreationSuccess();
        navigate(`/assistants/${_id}`, { state: { assistant: response.data } });
      }
    } catch (e) {
      message.destroy();
      message.error(e.response.data?.msg || e.response.data?.message || e.response.data?.error || "Failed to create assistant");
      setLoading(false);
    }
  };

  return (
    <>
      <Dropdown>
        <DropdownButton>
          <PlusIcon />
          Create Assistant
          <ChevronDownIcon />
        </DropdownButton>

        <DropdownMenu>
          {!allowAssistantCreation && (
            <DropdownSection>
              <DropdownHeading>
                <Tooltip title="Please contact support to create more assistants">
                  <div className="flex gap-x-2 items-center">
                    <p>Assistants Limit Reached</p>
                    <RiInformation2Line className="size-4" />
                  </div>
                </Tooltip>
              </DropdownHeading>
            </DropdownSection>
          )}
          {allowAssistantCreation && (
            <DropdownSection className="flex flex-col gap-y-2">
              {assistantTypes.map((type, index) => {
                return (
                  <DropdownItem
                    key={index}
                    onClick={() => handleDropdownItemClick(type.toLowerCase())}
                  >
                    <AssistantType type={type} />
                  </DropdownItem>
                );
              })}
            </DropdownSection>
          )}
        </DropdownMenu>
      </Dropdown>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
        <DialogTitle className="flex gap-x-2">
          {Icons[assistantData.assistantType]}
          {title[assistantData.assistantType]}
        </DialogTitle>
        <DialogBody>
          <Form
            inputField={assistantData}
            buttonInfo={buttonInfo}
            onSubmit={handleCreate}
            loading={loading}
            setLoading={setLoading}
            handleSecondary={() => setIsOpen(false)}
            template={template}
            schema={schema}
          />
        </DialogBody>
      </Dialog>
    </>
  );
}
